// src/pages/Packages.jsx
//
// Standalone page for the full packages/bundles catalogue - moved off the
// homepage (which now shows only a compact CTA, see
// components/sections/PackagesPreview.jsx) to keep the homepage short.
// PackagesSection itself is unchanged and still fully admin-driven
// (Admin > Packages) - relocated here, not duplicated or rebuilt.
import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import Meta from "../Meta";
import Reveal from "../components/Reveal";
import PackagesSection from "../components/Packages/PackagesSection";
import CTA from "../components/sections/CTA";
import { usePageContent } from "../lib/usePageContent";
import { useSiteSettings } from "../lib/useSiteSettings";

const DEFAULT_HEADING = "Solar Cleaning Packages & Bundles";
const DEFAULT_INTRO = "Compare our solar, roof and concrete cleaning packages, or bundle services together for better value.";

export default function Packages() {
  const settings = useSiteSettings();
  const page = usePageContent("packages");
  const heading = page?.heading || DEFAULT_HEADING;
  const intro = page?.intro || DEFAULT_INTRO;
  const seoTitle = page?.seoTitle || `Solar Cleaning Packages Sydney | ${settings.name}`;
  const seoDesc = page?.seoDescription || `Compare ${settings.name}'s solar panel cleaning, roof cleaning and concrete cleaning packages and bundles, with transparent pricing across Sydney.`;

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://horizonsolar.com.au/" },
      { "@type": "ListItem", position: 2, name: "Packages", item: "https://horizonsolar.com.au/packages" },
    ],
  };

  return (
    <>
      <Meta title={seoTitle} desc={seoDesc} path="/packages">
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Meta>

      <main className="pt-24 bg-[#02060c]">
        <section className="relative overflow-hidden py-16 md:py-20 border-b border-white/5">
          <div className="relative max-w-4xl mx-auto px-6 text-center">
            <nav aria-label="breadcrumb" className="flex items-center justify-center gap-1.5 text-sm text-white/40 mb-6">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-white/70">Packages</span>
            </nav>
            <span className="text-[#22d3ee] tracking-widest text-sm font-semibold uppercase">Packages</span>
            <h1 className="mt-3 text-4xl md:text-6xl font-extrabold text-white tracking-tight leading-tight">{heading}</h1>
            <p className="mt-4 text-lg text-white/60 max-w-2xl mx-auto">{intro}</p>
          </div>
        </section>

        <Reveal><PackagesSection /></Reveal>
        <Reveal><CTA /></Reveal>
      </main>
    </>
  );
}
